import React, { useRef, useState, useEffect } from 'react';
import { Card, Form, Row, Col, ButtonGroup, ProgressBar } from 'react-bootstrap';
import { useFormikContext, Formik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Button } from '@components';
import { FILE_MANAGER_API, BaseGatewayImage } from '@app/utils/apiUrl';
import { getToken } from '@app/utils/helper';
import { acceptDoc } from '@app/utils/define';

// --- CÁC HÀM TIỆN ÍCH HỖ TRỢ UPLOAD FILE ---
const isImageFile = (filePath = '') => {
    return /\.(jpeg|jpg|gif|png|jpe)$/i.test(filePath);
};

const uploadFileToServer = async ({ fileUpload, setCompleted }) => {
    const token = await getToken();
    const config = {
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setCompleted(percentCompleted);
        },
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `bearer ${token}` }
    };
    const data = new FormData();
    data.append('file', fileUpload);
    return axios.post(FILE_MANAGER_API.UPLOAD, data, config);
};

// Component để theo dõi và xử lý logic ẩn hiện/reset trường dữ liệu
const FormObserver = () => {
    const { values, setFieldValue } = useFormikContext();
    
    useEffect(() => {
        if (values.loaiVanBang === 'VanBangTrongNuoc') {
            setFieldValue('isHopPhapHoa', false);
            setFieldValue('soQDCongNhan', '');
        }
    }, [values.loaiVanBang, setFieldValue]);

    return null;
};
const FileMinhChungField = ({ value, onChange, disabled = false }) => {
    const inputUpload = useRef(null);
    const [completed, setCompleted] = useState(0);
    const [fileInfo, setFileInfo] = useState({ id: '', filePath: '', name: '' });

    useEffect(() => {
        setFileInfo({ id: value?.id || '', filePath: value?.filePath || '', name: value?.name || '' });
    }, [value]);

    const handleUploadFile = async (event) => {
        if (disabled) return;
        const fileUpload = event.target.files?.[0];
        if (!fileUpload) return;
        const fileSize = fileUpload.size / 1024 / 1024;
        if (fileSize > 20) {
            toast.error('Kích thước file quá lớn, hãy thử lại với tệp nhỏ hơn 20MB');
            return;
        }

        try {
            const result = await uploadFileToServer({ fileUpload, setCompleted });
            const uploaded = result?.data?.[0];
            if (!uploaded) {
                toast.error('Không nhận được dữ liệu file sau khi tải lên');
                setCompleted(0);
                return;
            }
            const normalized = {
                id: uploaded.id || uploaded.Id || '',
                filePath: uploaded.filePath || uploaded.FilePath || '',
                name: uploaded.name || uploaded.Name || fileUpload.name
            };
            setFileInfo(normalized);
            onChange(normalized);
            setCompleted(0);
            toast.success('Tải file thành công!');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Tải file thất bại');
            setCompleted(0);
        } finally {
            if (inputUpload.current) inputUpload.current.value = '';
        }
    };

    const handleRemove = () => {
        if (disabled) return;
        const emptyFile = { id: '', filePath: '', name: '' };
        setFileInfo(emptyFile);
        onChange(emptyFile);
    };

    // SỬA LỖI: Hỗ trợ mở file bằng ID nếu không có đường dẫn (filePath)
    const handleOpenFile = () => {
        if (fileInfo.filePath) {
            window.open(`${BaseGatewayImage}${fileInfo.filePath}`, '_blank');
        } else if (fileInfo.id) {
            // Fallback mở bằng API FileManager giống như ở màn hình Danh sách
            window.open(`${BaseGatewayImage}/api/v1/FileManager/${fileInfo.id}`, '_blank');
        }
    };

    // SỬA LỖI: Xác định xem đã có file hay chưa (chỉ cần có ID là được)
    const hasFile = !!(fileInfo.filePath || fileInfo.id);
    const isUploaded = hasFile && completed === 0;

    const renderImagePreview = () => {
        if (!fileInfo.filePath || !isImageFile(fileInfo.filePath)) return null;
        return (
            <div style={{
                border: '1px solid #dee2e6', borderRadius: '14px',
                padding: '12px', background: '#fff', marginBottom: '10px', textAlign: 'left'
            }}>
                <img src={`${BaseGatewayImage}${fileInfo.filePath}`} alt={fileInfo.name || 'file minh chứng'}
                    style={{ maxWidth: '240px', maxHeight: '180px', objectFit: 'contain', borderRadius: '8px' }} />
            </div>
        );
    };

    // SỬA LỖI: Hiển thị text "Đã có tệp đính kèm" nếu API chỉ trả về ID mà không trả về Tên file
    const displayValue = fileInfo.name || 
                        (fileInfo.filePath ? fileInfo.filePath.split('/').pop() : '') || 
                        (fileInfo.id ? 'Đã có tệp đính kèm' : '');

    return (
        <>
            {renderImagePreview()}
            <div style={{ border: '1px solid #dee2e6', borderRadius: '14px', padding: '16px', background: '#fff' }}>
                <div className="d-flex flex-wrap align-items-center mb-3">
                    {!disabled && (
                        <Button type="button" theme="primary" onClick={() => inputUpload.current?.click()}>Upload</Button>
                    )}
                    {!disabled && <div className="mx-3 text-muted">Dung lượng tối đa: 20MB</div>}

                    {/* SỬA LỖI: Nút bấm sáng lên khi có file (dựa vào hasFile thay vì fileInfo.filePath) */}
                    <Button type="button" theme="secondary" onClick={handleOpenFile} disabled={!hasFile}>
                        Xem
                    </Button>

                    {!disabled && (
                        <Button type="button" theme="danger" onClick={handleRemove} disabled={!hasFile} style={{ marginLeft: '8px' }}>Xóa</Button>
                    )}
                </div>
                {!disabled && <Form.Control type="file" hidden accept={acceptDoc} ref={inputUpload} onChange={handleUploadFile} />}
                
                {/* SỬA LỖI: Hiển thị giá trị */}
                <Form.Control value={displayValue} readOnly placeholder="Chưa có file minh chứng" />

                {!disabled && <ProgressBar className="mt-3" style={{ display: isUploaded ? 'none' : 'flex' }} variant="success" animated now={completed} />}
            </div>
        </>
    );
};

// --- SCHEMA VALIDATE ---
const schema = yup.object().shape({
    tenVanBang: yup
        .string()
        .required('Tên văn bằng không được để trống')
        .max(255, 'Tên văn bằng không được vượt quá 255 ký tự'),
    loaiVanBang: yup
        .string()
        .required('Vui lòng chọn loại văn bằng'),
    chuyenNganh: yup
        .string()
        .required('Chuyên ngành không được để trống')
        .max(255, 'Chuyên ngành không được vượt quá 255 ký tự'),
    coSoCapBang: yup
        .string()
        .required('Cơ sở cấp bằng không được để trống')
        .max(255, 'Cơ sở cấp bằng không được vượt quá 255 ký tự'),
    quocGiaCap: yup
        .string()
        .required('Quốc gia cấp không được để trống')
        .max(255, 'Quốc gia cấp không được vượt quá 255 ký tự'),
    soHieuVanBang: yup
        .string()
        .required('Số hiệu văn bằng không được để trống')
        .max(255, 'Số hiệu văn bằng không được vượt quá 255 ký tự'),
    ngayCap: yup
        .string()
        .required('Ngày cấp không được để trống'),
});

// --- MAIN COMPONENT ---
const VanBangForm = ({
    title,
    submitText,
    initialValues,
    onSubmit,
    onCancel,
    isSubmitting
}) => {
    return (
        <Card style={{ borderRadius: '16px' }}>
            <Card.Header as="h5" style={{ padding: '16px 20px', fontWeight: 700, background: '#fff', borderBottom: '1px solid #ebedf2' }}>
                {title}
            </Card.Header>
            <Card.Body style={{ background: '#fff', padding: '24px' }}>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                >
                    {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <FormObserver />
                            <h6 style={{ color: '#007bff', fontWeight: 600, marginBottom: '20px' }}>Thông tin văn bằng</h6>

                            {/* DÒNG 1 */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Tên văn bằng <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="tenVanBang"
                                            value={values.tenVanBang}
                                            onChange={handleChange}
                                            isInvalid={touched.tenVanBang && !!errors.tenVanBang}
                                        >
                                            <option value="">-- Chọn tên văn bằng --</option>
                                            <option value="Tiến sĩ">Tiến sĩ</option>
                                            <option value="Thạc sĩ">Thạc sĩ</option>
                                            <option value="Cử nhân">Cử nhân</option>
                                            <option value="Khác">Khác</option>
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.tenVanBang}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Số hiệu văn bằng <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            name="soHieuVanBang"
                                            value={values.soHieuVanBang}
                                            onChange={handleChange}
                                            isInvalid={touched.soHieuVanBang && !!errors.soHieuVanBang}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.soHieuVanBang}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* DÒNG 2 */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Loại văn bằng <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="loaiVanBang"
                                            value={values.loaiVanBang || ''} 
                                            onChange={handleChange}
                                            isInvalid={touched.loaiVanBang && !!errors.loaiVanBang}
                                        >
                                            <option value="">-- Chọn loại văn bằng --</option>
                                            <option value="NuocNgoai">Văn bằng nước ngoài</option>
                                            <option value="VanBangTrongNuoc">Văn bằng trong nước (ngoại ngữ)</option>
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.loaiVanBang}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Chuyên ngành <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            name="chuyenNganh"
                                            value={values.chuyenNganh}
                                            onChange={handleChange}
                                            isInvalid={touched.chuyenNganh && !!errors.chuyenNganh}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.chuyenNganh}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* DÒNG 3 */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Cơ sở cấp bằng <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            name="coSoCapBang"
                                            value={values.coSoCapBang}
                                            onChange={handleChange}
                                            isInvalid={touched.coSoCapBang && !!errors.coSoCapBang}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.coSoCapBang}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Quốc gia cấp <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="quocGiaCap"
                                            value={values.quocGiaCap}
                                            onChange={handleChange}
                                            isInvalid={touched.quocGiaCap && !!errors.quocGiaCap}
                                        >
                                            <option value="">-- Chọn quốc gia cấp --</option>
                                            <option value="Anh">Anh</option>
                                            <option value="Đức">Đức</option>
                                            <option value="Pháp">Pháp</option>
                                            <option value="Nhật">Nhật</option>
                                            <option value="Trung Quốc">Trung Quốc</option>
                                            <option value="Nga">Nga</option>
                                            <option value="Tây Ba Nha">Tây Ba Nha</option>
                                            <option value="Việt Nam">Việt Nam</option>
                                            <option value="Khác">Khác</option>
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.quocGiaCap}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* DÒNG 4 */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Ngày cấp <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="ngayCap"
                                            value={values.ngayCap ? values.ngayCap.split('T')[0] : ''}
                                            onChange={handleChange}
                                            isInvalid={touched.ngayCap && !!errors.ngayCap}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.ngayCap}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Xếp loại</Form.Label>
                                        <Form.Control
                                            name="xepLoai"
                                            value={values.xepLoai}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* DÒNG 5 - Chỉ hiện nếu đã chọn loại văn bằng và KHÔNG PHẢI Văn bằng trong nước */}
                            {values.loaiVanBang && values.loaiVanBang !== 'VanBangTrongNuoc' && (
                                <Row>
                                    <Col md={6} className="d-flex align-items-center">
                                        <Form.Group className="mb-3 w-100" style={{ marginTop: '30px', paddingLeft: '4px' }}>
                                            <Form.Check
                                                type="checkbox"
                                                label={<span style={{ fontWeight: 700, marginLeft: '8px' }}>Đã được Hợp pháp hóa lãnh sự</span>}
                                                name="isHopPhapHoa"
                                                checked={values.isHopPhapHoa}
                                                onChange={(e) => setFieldValue('isHopPhapHoa', e.target.checked)}
                                                style={{ transform: 'scale(1.15)', transformOrigin: 'left center' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-500">Số QĐ công nhận</Form.Label>
                                            <Form.Control
                                                name="soQDCongNhan"
                                                value={values.soQDCongNhan}
                                                onChange={handleChange}
                                                disabled={!values.isHopPhapHoa}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}

                            {/* FILE MINH CHỨNG */}
                            <Form.Group className="mb-3 mt-2">
                                <Form.Label className="fw-500">File minh chứng</Form.Label>
                                <FileMinhChungField
                                    value={{
                                        id: values.fileMinhChungId,
                                        filePath: values.fileMinhChungPath,
                                        name: values.fileMinhChungName
                                    }}
                                    onChange={(file) => {
                                        setFieldValue('fileMinhChungId', file?.id || '');
                                        setFieldValue('fileMinhChungPath', file?.filePath || '');
                                        setFieldValue('fileMinhChungName', file?.name || '');
                                    }}
                                />
                            </Form.Group>

                            {/* GHI CHÚ */}
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-500">Ghi chú</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="ghiChu"
                                    value={values.ghiChu}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            {/* BUTTONS */}
                            <Form.Group as={Col} className="mb-2 mt-4">
                                <div className="d-flex justify-content-center">
                                    <ButtonGroup>
                                        <Button type="submit" theme="primary" disabled={isSubmitting}>
                                            {isSubmitting ? 'Đang xử lý...' : submitText}
                                        </Button>
                                        <div style={{ width: '15px' }}></div>
                                        <Button theme="secondary" onClick={onCancel} type="button">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            </Form.Group>

                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default VanBangForm;